import { getUnifiedValue } from '@src/utility/compositionSettings';

import FontFamilyVariantDropdown from '../../../common/FontFamilyVariantDropdown';
import { ControlFieldLabel } from './ControlField';

import type {
  ControlConfiguration,
  ControlRenderProps,
  FontControlConfiguration,
} from '../types';

export const FontControl = ({
  control,
  activeElement,
  modifyProperty,
  compositionMeta,
}: ControlRenderProps<FontControlConfiguration>) => {
  const { unifiedValue, unifiedFontWeight } = getUnifiedValue(
    control,
    activeElement,
    compositionMeta,
  );
  const defaultFont = typeof unifiedValue === 'string' ? unifiedValue : 'Inter';
  return (
    <>
      <ControlFieldLabel>{control.label}</ControlFieldLabel>
      {/*
       * Picker rendered with the gold-standard pattern (chevron-collapsed
       * families with inline variant rows, each variant rendered in its
       * own weight). See `FontFamilyVariantDropdown` for the closed-state
       * "Family · Variant" display.
       */}
      <FontFamilyVariantDropdown
        action={(_p, value, variant) => {
          // Apply the chosen face (postScriptName) to font_family.
          modifyProperty(control, value);
          // A brand variant identifies a face by family + weight + style, and
          // the renderer registers each face under (postScriptName, weight,
          // style) — so ALL THREE must be written together. A stale font_weight
          // or font_style makes the canvas resolve the wrong (or fallback) face
          // (e.g. an italic pick rendering upright, per the reported bug).
          // Writing them here also lets a single Font control fully specify the
          // face — no separate Font Weight control. Popular fonts pass no
          // variant and keep their existing weight/style.
          if (variant) {
            modifyProperty(
              { ...control, property: 'font_weight' } as ControlConfiguration,
              String(variant.weight),
            );
            // Popular-font weight picks carry no style (italic is a separate
            // concern there); brand variants specify their face's own.
            if (variant.style) {
              modifyProperty(
                { ...control, property: 'font_style' } as ControlConfiguration,
                variant.style,
              );
            }
          }
        }}
        propertyName="font_family"
        defaultValue={defaultFont}
        // Lets the picker highlight the active weight row for popular fonts
        // (brand variants match on postScriptName and don't need this).
        fontWeight={unifiedFontWeight}
        // Composition text (overlays / nametags / cards) is free-form, so the
        // Japanese fonts are always available here — not gated on the
        // transcript language the way captions are (PLAT-10420).
        alwaysShowJapaneseFonts
      />
    </>
  );
};
